<template>
  <div v-if="rows.length">
    <!-- 見出し（この種類の説明） -->
    <div v-if="captionText" class="mt-5 mb-2 text-sm font-bold text-gray-700 whitespace-pre-wrap">{{ captionText }}</div>

    <!-- この種類の全部の日に同じ操作を一括で入れる（操作を選べるときで、2日以上あるとき）
         押せるのは、その日の選択肢にある操作だけ入る（例：代勤がかぶる日に「何もしない」は入らない） -->
    <div v-if="!disabled && rows.length > 1" class="bulk-row">
      <span class="bulk-label">一括変更：</span>
      <button
        v-for="(b, bi) in bulkButtons"
        :key="'bulk' + bi"
        type="button"
        class="action bulk"
        :class="isAllChecked(b) ? 'selected' : ''"
        v-tooltip="'この枠の全部の日を「' + b.name + '」にします（その操作を選べない日はそのまま）'"
        @click="bulkSelect(b)"
      >
        {{ b.name }}
      </button>
    </div>

    <!-- 雇用指示の1行 = 表の1行。列は確認が要る日だけ（AKBSの勤務表と同じ見た目に寄せる）。セルの幅は固定
         各セルの先頭にその行のその日の勤務時間を出し、一番下の行で日ごとに操作を選ぶ -->
    <div v-for="g in groups" :key="g.key" class="mb-4 border-2 rounded overflow-x-auto" :class="borderClass(g.kind)">
      <table class="check-table">
        <thead>
          <tr>
            <th class="head-col"></th>
            <th v-for="d in g.days" :key="d" class="day-col">{{ dayText(d) }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(r, ri) in g.rows">
            <tr :key="g.key + 'r' + ri" :class="r.role">
              <!-- 行の見出し（物件名・契約コード） -->
              <th class="head-col">
                <div class="text-gray-500 whitespace-pre-wrap">{{ r.label }}</div>
                <div class="site-name">{{ r.siteName }}</div>
                <div v-if="r.cdContract" class="text-gray-500">（契約：{{ r.cdContract }}）</div>
                <div v-if="r.warn" class="font-bold text-red-600 mt-1">{{ r.warn }}</div>
              </th>

              <td v-for="d in g.days" :key="d" class="day-col">
                <!-- その行のその日の勤務時間（雇用指示の開始・終了。勤務が無い日は - ） -->
                <div v-if="r.times" class="work-time">{{ r.times[d] || '-' }}</div>

                <template v-for="(it, ii) in (r.cells[d] || [])">
                  <!-- 登録情報（休み・振替・代勤）。reg は元の行、dst は引き継ぎ先の行
                       「引き継ぐ」を選ぶと元の行は点線で薄く残り、引き継ぎ先の行に濃く出る -->
                  <div v-if="(it.type === 'reg' || it.type === 'dst') && regVisible(it)" :key="ii" class="cell-item" :class="regClass(it)">
                    <template v-if="it.row.table === 'status'">
                      <!-- 振替で元日が期間の外のとき（変更日をまたぐ）は振替先の日に出す -->
                      <template v-if="isExchangeOnly(it.row)">
                        <div class="badge purple">振替先</div>
                        <div class="text-gray-600">振替元：{{ slashDay(it.row.day) }}</div>
                        <!-- 振替元が変更日より前のペアは引き継がない（元の休みは変更前の行のもの。振替先の分は新しい行で代勤を登録する） -->
                        <div v-if="it.row.kind === 'takeover' && it.type === 'reg'" class="overlap-note">振替元が変更日より前なので引き継げません。<br>振替先の分は新しい行で<br>代勤登録してください。</div>
                        <!-- 振替元が作業終了日以前のペアは削除しない（消すと終了日以前の正しい休みも一緒に消える） -->
                        <div v-else-if="it.row.kind === 'end'" class="overlap-note">振替元が作業終了日以前なので削除できません。<br>振替先の分は代勤で<br>埋めてください。</div>
                      </template>
                      <template v-else>
                        <div class="badge" :class="holiColor(it.row.status)">{{ it.row.status }}</div>
                        <div v-if="it.row.exchangeDay" class="text-gray-600">振替先：{{ exchangeText(it.row) }}</div>
                      </template>
                    </template>
                    <template v-else>
                      <div class="op-box">
                        <span class="op-badge">代勤</span>
                        <div class="badge" :class="opColor(it.row.status)">
                          <div>{{ it.row.startTime }} - {{ it.row.endTime }}（{{ it.row.hours }}h）</div>
                          <div v-if="it.row.workInstead">{{ it.row.workInstead }}</div>
                          <div>{{ it.row.status }}</div>
                        </div>
                      </div>
                    </template>
                    <div v-if="it.row.note" class="note">メモ： {{ it.row.note }}</div>
                    <!-- 選んだ操作の結果（引き継ぐは移動で見せる） -->
                    <div v-if="it.type === 'reg' && it.row.action === 'delete'" class="preview-delete">削除します</div>
                    <div v-else-if="it.type === 'reg' && it.row.action === 'task'" class="preview-task">確認メインタスク</div>
                  </div>

                  <!-- かぶり：他の物件で休み登録しているもの -->
                  <div v-else-if="it.type === 'holi'" :key="ii" class="cell-item">
                    <div class="badge" :class="holiColor(it.d.status)">{{ it.d.status }}</div>
                    <div v-if="it.d.note" class="note">メモ： {{ it.d.note }}</div>
                  </div>

                  <!-- かぶり：本人が代勤に行く予定（時間帯がかぶる）。赤枠で囲み、本人の名前を入れて他と区別する -->
                  <div v-else-if="it.type === 'op'" :key="ii" class="cell-item overlap-cell">
                    <div class="overlap-title">！時間帯かぶり</div>
                    <div class="op-box">
                      <span class="op-badge">代勤</span>
                      <div class="badge" :class="opColor(it.d.status)">
                        <div>{{ it.d.startTime }} - {{ it.d.endTime }}（{{ it.d.hours }}h）</div>
                        <div v-if="staffName">{{ staffName }}</div>
                        <div>{{ it.d.status }}</div>
                      </div>
                    </div>
                    <!-- 確定している代勤（●：代勤OK・休み取り消し・スポット作業）か、まだ確定していない（前日に ●：代勤OK へ更新する）かを示す -->
                    <div class="op-confirm" :class="it.d.confirmed ? 'yes' : 'no'">{{ it.d.confirmed ? '確定した代勤' : '未確定の代勤' }}</div>
                    <div v-if="it.d.note" class="note">メモ： {{ it.d.note }}</div>
                  </div>

                  <!-- かぶり：今回登録した行のその日の勤務（時間は先頭に出している）。選んだ操作の結果を同じセルに見せる
                       時間帯がかぶる代勤がある日は赤枠にして、どの代勤とかぶるかを書く -->
                  <div v-else-if="it.type === 'work'" :key="ii" class="cell-item" :class="(it.row.op || []).length ? 'overlap-cell' : ''">
                    <div v-if="it.row.isHolidayTime" class="font-bold text-red-700 text-center">祝日勤務（短い時間）</div>
                    <!-- 確定した代勤とかぶる日は休み登録を勧める（確定した代勤はもう更新しないので、ここで防ぐ）。未確定だけなら文は出さない（代勤の箱の「未確定の代勤」で分かる） -->
                    <div v-if="(it.row.op || []).some(d => d.confirmed)" class="overlap-note">確定した代勤とかぶっているので<br>休み登録を推奨します。</div>
                    <!-- 代勤と勤務の時間帯が同じでないときは、そのことも知らせる -->
                    <div v-for="(d, di) in (it.row.op || []).filter(d => !isSameTime(d, it.row))" :key="'ov' + di" class="overlap-diff">※代勤は {{ d.startTime }} - {{ d.endTime }}で<br>勤務と時間帯が一部違うので注意してください。</div>
                    <!-- 契約内容変更で、その曜日の勤務を変えていないときだけ知らせる（そのままで良い可能性がある） -->
                    <div v-if="it.row.changeWeek === false" class="text-gray-600">※{{ weekName(it.row.day) }}曜日は勤務変更なし</div>
                    <div v-if="it.row.action === 'insert'" class="badge mt-1" :class="holiColor(insertName(it.row))">{{ insertName(it.row) }}</div>
                    <div v-else-if="it.row.action === 'task'" class="preview-task">確認メインタスク</div>
                  </div>
                </template>
              </td>
            </tr>

            <!-- 引き継ぎだけ：変更前の行から変更後の行へ、の太い矢印。「引き継ぐ」を選んだ日だけ赤くする -->
            <tr v-if="g.arrow && ri === g.rows.length - 2" :key="g.key + 'a' + ri" class="arrow-row">
              <td class="head-col"></td>
              <td v-for="d in g.days" :key="d" class="text-center" :class="arrowClass(g, d)">
                <svg class="arrow-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 23 L2 12 H8 V1 H16 V12 H22 Z" fill="currentColor" />
                </svg>
              </td>
            </tr>
          </template>

          <!-- 操作の選択（一番下の行に日ごと。選択肢はすべて丸いボタンで見せる）
               同じ日の代勤が複数あっても操作は1つ（o.rows にまとめて全部に同じ操作を入れる） -->
          <tr class="ops-row">
            <th class="head-col">{{ disabled ? '選択した操作' : '操作を選んでください' }}</th>
            <td v-for="d in g.days" :key="d" class="day-col">
              <div v-for="(o, oi) in (g.ops[d] || [])" :key="oi" class="op-group">
                <!-- 同じ日に2つ以上あるときだけ、どれの操作かを書く -->
                <div v-if="g.ops[d].length > 1" class="op-label">{{ o.label }}</div>
                <button
                  v-for="(b, bi) in buttons(o.rows[0])"
                  :key="bi"
                  type="button"
                  class="action"
                  :class="isChecked(o.rows[0], b) ? 'selected' : ''"
                  :disabled="disabled"
                  @click="selectAll(o, b)"
                >{{ b.name }}</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/**
 * 確認一覧の種類（バックの getWorkingCheckList の kind と同じ）。モーダルと登録済み画面の両方で使う
 */
export const WORKING_CHECK_KINDS = [
  { key: 'takeover', name: '引き継ぎ', caption: '契約変更前の行に登録されている休み・代勤・振替です。新しい行に引き継ぐか選んでください。' },
  { key: 'overlap', name: 'かぶり', caption: '今回登録した物件の勤務日に、他で休み登録・代勤登録があります。' },
  { key: 'end', name: '作業終了後', caption: '作業終了日より後に登録されている休み・振替です。削除するか選んでください。' },
  { key: 'retire', name: '退職後の代勤', caption: '退職日より後に本人が代勤に行く予定があります。' }
]

/**
 * 確認一覧を種類ごとに分ける（無い種類は返さない）
 * @param {Array} list 確認一覧
 */
export function groupByKind (list) {
  const rows = Array.isArray(list) ? list : []
  return WORKING_CHECK_KINDS
    .map(k => ({ ...k, rows: rows.filter(r => r.kind === k.key) }))
    .filter(g => g.rows.length)
}

// 操作の選択肢（action はバックの saveCheckData と同じ文字）
const BTN_NONE = { name: '何もしない', action: 'none' }
const BTN_TASK = { name: '確認メインタスクを作る', action: 'task' }
const BTN_MOVE = { name: '新しい行に引き継ぐ', action: 'move' }
const BTN_DELETE = { name: '削除する', action: 'delete' }

// かぶりで今回登録した行に入れる休みの名前（insertStatus → 名前）
const INSERT_NAMES = { 0: '休み', 1: '有休', 8: '半有休' }

// 曜日名（日曜始まり）
const WEEK_NAMES = ['日', '月', '火', '水', '木', '金', '土']

export default {
  props: {
    // 表示するリスト（同じ種類のもの。1件 = 登録情報1件、かぶりは1日分）
    list: {
      type: Array,
      default: () => []
    },
    // 見出し（この種類の説明）
    caption: {
      type: String,
      default: ''
    },
    // 以前はラジオボタンのグループ名に使っていた。ボタンにしたので使わないが、呼び出し側を変えないため残す
    radioName: {
      type: String,
      default: 'workingCheck'
    },
    // 操作を選べない（登録済みの画面で選んだ操作を見せるだけのとき）
    disabled: {
      type: Boolean,
      default: false
    },
    // 清掃員名（かぶりで本人が代勤に行く予定の箱に出す）
    staffName: {
      type: String,
      default: ''
    }
  },

  computed: {
    /**
     * リスト（未設定・オブジェクト渡し対策）
     */
    rows() {
      return Array.isArray(this.list) ? this.list : []
    },

    /**
     * 一括で選べる操作（この枠の全部の日の選択肢を合わせたもの。出てきた順）
     */
    bulkButtons() {
      const result = []
      for (const row of this.rows) {
        for (const b of this.buttons(row)) {
          if (!result.some(x => this.sameButton(x, b))) {
            result.push(b)
          }
        }
      }
      return result
    },

    /**
     * 見出しの文。操作を選べない（登録済みの画面・メインタスク）ときは「〜か選んでください。」の文を外す（もう操作できないため）
     */
    captionText() {
      if (!this.caption) return ''
      return this.disabled ? this.caption.replace(/[^。]*選んでください。/g, '').trim() : this.caption
    },

    /**
     * 表にする単位（雇用指示の1行ごと）
     * { key, kind, days: ['2026-07-18', ...], rows: [{ label, siteName, cdContract, warn, role, times: { day: '9:00 - 18:00' }, cells: { day: [item] } }], arrow, ops: { day: [{ label, rows }] } }
     * item は type で描き分ける（reg：登録情報、dst：引き継ぎ先、holi・op：かぶりの相手、work：かぶりの今回の行）
     * times（その日の勤務時間）は雇用指示の行だけ持つ（かぶりの相手・退職後の代勤は無い）
     * ops は日ごとの操作。同じ日の代勤は1つにまとめる（rows が複数）
     */
    groups() {
      let result = []
      for (const k of WORKING_CHECK_KINDS) {
        const rows = this.rows.filter(r => r.kind === k.key)
        if (!rows.length) {
          continue
        }
        switch (k.key) {
          case 'takeover':
            result = result.concat(this.takeoverGroups(rows))
            break
          case 'overlap':
            result = result.concat(this.overlapGroups(rows))
            break
          default:
            result = result.concat(this.singleGroups(rows, k.key))
            break
        }
      }
      return result
    }
  },

  methods: {
    /**
     * 引き継ぎ：新しい行ごとに表を作る。上が変更前の行、下が新しい行
     * 「引き継ぐ」を選んだものは上に点線で薄く残り、下に濃く出る（regVisible・regClass）
     * @param {Array} rows 引き継ぎの一覧
     */
    takeoverGroups(rows) {
      return this.groupBy(rows, r => String(r.newSequenceNumber)).map(g => {
        const days = this.uniqDays(g.rows)
        const top = { label: '契約変更前の行\n（本来は表示されません）', siteName: g.rows[0].siteName, cdContract: g.rows[0].cdContract, role: 'src', times: {}, cells: {} }
        const bottom = { label: '今回登録した行（変更後）', siteName: g.rows[0].siteName, cdContract: g.rows[0].newCdContract, role: 'dst', times: {}, cells: {} }
        const ops = {}
        for (const row of g.rows) {
          const day = this.showDay(row)
          top.times[day] = this.timeText(row.workStart, row.workEnd, row.workHours)
          bottom.times[day] = this.timeText(row.newWorkStart, row.newWorkEnd, row.newWorkHours)
          this.push(top.cells, day, { type: 'reg', row })
          this.push(bottom.cells, day, { type: 'dst', row })
          this.addOp(ops, day, row)
        }
        return { key: 'takeover' + g.key, kind: 'takeover', days, rows: [top, bottom], arrow: true, ops }
      })
    },

    /**
     * かぶり：今回登録した行ごとに表を作る。上が相手の物件（休み登録・代勤）、下が今回登録した行（勤務）
     * @param {Array} rows かぶりの一覧（1件 = 1日）
     */
    overlapGroups(rows) {
      return this.groupBy(rows, r => String(r.sequenceNumber)).map(g => {
        const days = this.uniqDays(g.rows)
        // 相手の物件ごとの行（休み登録と代勤は別の行にする）
        const others = []
        const other = (type, siteName) => {
          let r = others.find(o => o.type === type && o.siteName === siteName)
          if (!r) {
            // 休み登録している物件の行は、その日の勤務時間（休み登録の雇用指示の時間帯・実働）も出す（times。表の上段）
            r = type === 'holi'
              ? { type, label: '休み登録している物件', siteName, role: 'src', times: {}, cells: {} }
              : { type, label: '代勤に行く予定の物件', siteName, warn: '！時間帯がかぶっています！', role: 'src', cells: {} }
            others.push(r)
          }
          return r
        }
        const bottom = { label: '今回登録した行', siteName: g.rows[0].siteName, cdContract: g.rows[0].cdContract, role: 'dst', times: {}, cells: {} }
        const ops = {}

        for (const row of g.rows) {
          for (const d of row.holi || []) {
            const r = other('holi', d.siteName)
            r.times[row.day] = this.timeText(d.workStart, d.workEnd, d.workHours)
            this.push(r.cells, row.day, { type: 'holi', d })
          }
          for (const d of row.op || []) {
            this.push(other('op', d.siteName).cells, row.day, { type: 'op', d })
          }
          bottom.times[row.day] = this.timeText(row.startTime, row.endTime, row.hours)
          this.push(bottom.cells, row.day, { type: 'work', row })
          this.push(ops, row.day, { label: '', rows: [row] })
        }
        // 矢印は付けない（引き継ぎのように移すものではないため）
        return { key: 'overlap' + g.key, kind: 'overlap', days, rows: others.concat([bottom]), arrow: false, ops }
      })
    },

    /**
     * 作業終了後・退職後の代勤：行は1本。結果は同じセルに見せる
     * @param {Array} rows 一覧
     * @param {String} kind 種類
     */
    singleGroups(rows, kind) {
      const isRetire = kind === 'retire'
      return this.groupBy(rows, r => String(isRetire ? r.cdSite : r.sequenceNumber)).map(g => {
        const days = this.uniqDays(g.rows)
        const row = {
          label: isRetire ? '本人が代勤に行く予定の物件' : '作業終了した行',
          siteName: g.rows[0].siteName,
          cdContract: isRetire ? '' : g.rows[0].cdContract,
          role: 'single',
          // 退職後の代勤は本人の雇用指示の行ではないので勤務時間は無い
          times: isRetire ? null : {},
          cells: {}
        }
        const ops = {}
        for (const r of g.rows) {
          const day = this.showDay(r)
          if (row.times) {
            row.times[day] = this.timeText(r.workStart, r.workEnd, r.workHours)
          }
          this.push(row.cells, day, { type: 'reg', row: r })
          this.addOp(ops, day, r)
        }
        return { key: kind + g.key, kind, days, rows: [row], arrow: false, ops }
      })
    },

    /**
     * 登録情報を表に出すか。引き継ぎ先の行（dst）は「引き継ぐ」を選んだときだけ出す
     * @param it セルの項目
     */
    regVisible(it) {
      return it.type !== 'dst' || it.row.action === 'move'
    },

    /**
     * 登録情報の見た目。引き継ぎ元は「引き継ぐ」を選ぶと点線で薄く残す
     * @param it セルの項目
     */
    regClass(it) {
      if (it.type === 'reg' && it.row.kind === 'takeover' && it.row.action === 'move') {
        return 'ghost'
      }
      return it.type === 'reg' && it.row.action === 'delete' ? 'deleted' : ''
    },

    /**
     * 日ごとの操作に登録情報を足す
     * 休み登録は1件ずつ、代勤は同じ日なら1つにまとめる（複数あっても同じ操作をする）
     * @param {Object} ops 日付 → 操作の配列
     * @param {String} day 日付
     * @param row 登録情報
     */
    addOp(ops, day, row) {
      if (row.table === 'operation') {
        const found = (ops[day] || []).find(o => o.label === '代勤')
        if (found) {
          found.rows.push(row)
          return
        }
        this.push(ops, day, { label: '代勤', rows: [row] })
        return
      }
      this.push(ops, day, { label: this.isExchangeOnly(row) ? '振替先' : row.status, rows: [row] })
    },

    /**
     * 操作の選択肢を、まとめた登録情報すべてに入れる
     * @param o 操作（rows）
     * @param b 選択肢
     */
    selectAll(o, b) {
      for (const row of o.rows) {
        this.select(row, b)
      }
    },

    /**
     * 同じ操作のボタンか（action と、休み登録なら種別も同じ）
     */
    sameButton(a, b) {
      return a.action === b.action && (a.action !== 'insert' || Number(a.insertStatus) === Number(b.insertStatus))
    },

    /**
     * この枠の全部の日に同じ操作を入れる（その操作を選べない日はそのまま）
     * @param b 操作のボタン
     */
    bulkSelect(b) {
      for (const row of this.rows) {
        if (this.buttons(row).some(x => this.sameButton(x, b))) {
          this.select(row, b)
        }
      }
    },

    /**
     * この枠で、その操作を選べる日が全部その操作になっているか（一括ボタンの色用）
     * @param b 操作のボタン
     */
    isAllChecked(b) {
      const targets = this.rows.filter(row => this.buttons(row).some(x => this.sameButton(x, b)))
      return targets.length > 0 && targets.every(row => this.isChecked(row, b))
    },

    /**
     * 代勤と勤務の時間帯が同じか
     * @param d 代勤（startTime・endTime）
     * @param row 今回の勤務（startTime・endTime）
     */
    isSameTime(d, row) {
      return String(d.startTime || '') === String(row.startTime || '') && String(d.endTime || '') === String(row.endTime || '')
    },

    /**
     * 勤務時間の表示（9:00 - 18:00（7h）。勤務が無ければ空）
     * @param {String} start 開始
     * @param {String} end 終了
     * @param {String|Number} hours 実働時間（無ければ時間帯だけ）
     */
    timeText(start, end, hours) {
      if (!start && !end) {
        return ''
      }
      const h = hours !== null && hours !== undefined && hours !== '' ? `（${hours}h）` : ''
      return `${start || ''} - ${end || ''}${h}`
    },

    /**
     * キーごとにまとめる（出てきた順）
     * @param {Array} rows 一覧
     * @param {Function} keyOf キーを返す関数
     */
    groupBy(rows, keyOf) {
      const groups = []
      for (const row of rows) {
        const key = keyOf(row)
        let g = groups.find(x => x.key === key)
        if (!g) {
          g = { key, rows: [] }
          groups.push(g)
        }
        g.rows.push(row)
      }
      return groups
    },

    /**
     * 表に出す日（振替で元日が期間の外なら振替先の日。バックの display_day。無ければ day）
     * @param row
     */
    showDay(row) {
      return String(row.displayDay || row.day || '')
    },

    /**
     * 振替先の日に出しているか（元日が期間の外）
     * @param row
     */
    isExchangeOnly(row) {
      return !!row.displayDay && String(row.displayDay) !== String(row.day)
    },

    /**
     * 日付の一覧（重複なし・昇順）
     * @param {Array} rows 一覧
     */
    uniqDays(rows) {
      return [...new Set(rows.map(r => this.showDay(r)))].sort()
    },

    /**
     * 日付ごとの配列に項目を足す
     * @param {Object} map 日付 → 項目の配列
     * @param {String} day 日付
     * @param {Object} item 項目
     */
    push(map, day, item) {
      const key = String(day || '')
      if (!map[key]) {
        map[key] = []
      }
      map[key].push(item)
    },

    /**
     * 日付の見出し（2026/7/18(土)）
     * @param {String} day 日付（yyyy-mm-dd）
     */
    dayText(day) {
      const parts = String(day || '').split('-')
      if (parts.length !== 3) {
        return day
      }
      const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      const week = isNaN(dt.getTime()) ? '' : `(${WEEK_NAMES[dt.getDay()]})`
      return `${parts[0]}/${Number(parts[1])}/${Number(parts[2])}${week}`
    },

    /**
     * 曜日名（土）
     * @param {String} day 日付（yyyy-mm-dd）
     */
    weekName(day) {
      const parts = String(day || '').split('-')
      const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      return parts.length === 3 && !isNaN(dt.getTime()) ? WEEK_NAMES[dt.getDay()] : ''
    },

    /**
     * yyyy-mm-dd → yyyy/mm/dd
     * @param {String} day 日付
     */
    slashDay(day) {
      return String(day || '').replace(/-/g, '/')
    },

    /**
     * 矢印の色。引き継ぎは、その日に「引き継ぐ」を選んだものがあるときだけ赤くする
     * @param g 表
     * @param {String} d 日付
     */
    arrowClass(g, d) {
      const items = g.rows[0].cells[d] || []
      return items.some(it => it.row && it.row.action === 'move') ? 'text-red-600' : 'text-gray-300'
    },

    /**
     * 枠の色（退職後の代勤は必ず対応が要るので赤）
     * @param {String} kind 種類
     */
    borderClass(kind) {
      return kind === 'retire' ? 'border-red-400' : 'border-blue-400'
    },

    /**
     * 振替先の表示（2026/10/10 3時間30分）
     * @param row
     */
    exchangeText(row) {
      let text = this.slashDay(row.exchangeDay)
      const hour = row.exchangeHour
      const minute = row.exchangeMinute
      if (hour !== null && hour !== undefined && hour !== '') {
        text += ` ${hour}時間`
      }
      if (minute && Number(minute)) {
        text += `${minute}分`
      }
      return text
    },

    /**
     * かぶりで今回登録した行に入れる休みの名前
     * @param row
     */
    insertName(row) {
      return INSERT_NAMES[Number(row.insertStatus)] || '休み'
    },

    /**
     * 種類ごとの操作の選択肢
     * かぶりは、他で登録している休みに合わせて「有休」「半有休」の選択肢を足す
     * @param row
     */
    buttons(row) {
      switch (row.kind) {
        // 振替元が変更日より前のペア（振替先だけが変更日以降）は引き継げない
        // 休み登録（振替元）と振替先は同じキーで一緒に動くので、引き継ぐと変更日より前の休みが新しい行に付く。振替先の分は新しい行で代勤を登録してもらう
        case 'takeover':
          return this.isExchangeOnly(row) ? [BTN_NONE, BTN_TASK] : [BTN_MOVE, BTN_NONE, BTN_TASK]

        // 振替元が作業終了日以前のペア（振替先だけが終了日より後）は削除できない。休み登録（振替元）と振替先は同じキーで一緒に消えるので、終了日以前の正しい休みまで消えてしまう
        case 'end':
          return this.isExchangeOnly(row) ? [BTN_NONE, BTN_TASK] : [BTN_DELETE, BTN_NONE, BTN_TASK]

        // 退職後の代勤は「何もしない」を選べない（いない人に代勤に行かせても誰も掃除しない）
        case 'retire':
          return [BTN_DELETE, BTN_TASK]

        default: {
          // 確定している代勤（●：代勤OK・休み取り消し・スポット作業）と時間帯がかぶるときは「何もしない」を選べない（かぶったままになり、誰も気づかない）
          // 確定していない代勤（登録済みなど）だけなら、前日の更新で気づけるので「何もしない」も選べる
          const btn = (row.op || []).some(d => d.confirmed) ? [] : [BTN_NONE]
          btn.push(
            BTN_TASK,
            { name: '「休み」を登録する', action: 'insert', insertStatus: 0 }
          )
          const statuses = (row.holi || []).map(h => String(h.status || ''))
          if (statuses.some(s => s.includes('半有休') || s.includes('半日'))) {
            btn.push({ name: '「半有休」を登録する', action: 'insert', insertStatus: 8 })
          }
          if (statuses.some(s => s.includes('有休') && !s.includes('半有休') && !s.includes('半日'))) {
            btn.push({ name: '「有休」を登録する', action: 'insert', insertStatus: 1 })
          }
          return btn
        }
      }
    },

    /**
     * この選択肢が選ばれているか
     * @param row
     * @param b 選択肢
     */
    isChecked(row, b) {
      if (row.action !== b.action) {
        return false
      }
      return b.action !== 'insert' || Number(row.insertStatus) === b.insertStatus
    },

    /**
     * 選択肢を選んだ
     * @param row
     * @param b 選択肢
     */
    select(row, b) {
      row.action = b.action
      row.insertStatus = b.action === 'insert' ? b.insertStatus : null
      this.$emit('change', row)
    },

    /**
     * 休みの種別の色
     * @param status 種別名
     */
    holiColor(status) {
      switch (status) {
        case '欠員':
          return 'orange'
        case '要確認':
          return 'purple'
        default:
          return 'red'
      }
    },

    /**
     * 代勤の状況の色
     * @param status 状況名
     */
    opColor(status) {
      switch (status) {
        case '未登録':
        case '×：代勤行けない':
          return 'pink'
        case '代勤確定':
          return 'yellow'
        case '●：前日確認OK':
          return 'blue'
        case '×：連絡とれず':
          return 'red2'
        case '復帰確認':
          return 'gray'
        case '復帰不可・連絡取れず':
          return 'orange2'
        case '休み取り消し':
          return 'green'
        case 'スポット作業':
          return 'purple2'
        default:
          return ''
      }
    }
  }
}
</script>

<style scoped>
/* 表（左の見出し列を固定して横スクロール。sticky で罫線が消えないよう separate にする）
   物件名以外は小さい文字。列の幅は固定で、日が少なくても広げない */
.check-table {
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 11px;
  line-height: 1.3;
}

.check-table th,
.check-table td {
  border-right: 1px solid #c8c8c8;
  border-bottom: 1px solid #c8c8c8;
  padding: 3px 5px;
  vertical-align: top;
}

.check-table thead th {
  background: #f3f4f6;
  text-align: center;
  white-space: nowrap;
  font-weight: normal;
}

.head-col {
  width: 160px;
  min-width: 160px;
  max-width: 160px;
  text-align: center;
  background: #fafafa;
  position: sticky;
  left: 0;
  z-index: 1;
  font-weight: normal;
}

.site-name {
  font-size: 14px;
  font-weight: bold;
  color: #1d4ed8;
  /* 現場名2 の改行は残しつつ、長い物件名は列の幅で折り返す */
  white-space: pre-wrap;
  word-break: break-all;
  margin: 4px 0;
}

.day-col {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
}

/* 今回登録した行（変更後）は薄い青で目立たせる */
tr.dst td,
tr.dst th {
  background: #f0f5ff;
}

/* その行のその日の勤務時間（セルの先頭） */
.work-time {
  text-align: center;
  font-weight: bold;
  padding-bottom: 2px;
  margin-bottom: 3px;
  border-bottom: 1px solid #e5e7eb;
}

/* 矢印の行（太い矢印のアイコン） */
.arrow-row td {
  padding: 2px 0;
  height: 30px;
  line-height: 0;
  background: #fff;
}

.arrow-icon {
  display: inline-block;
  width: 26px;
  height: 26px;
}

.cell-item + .cell-item {
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px dashed #c8c8c8;
}

/* 登録情報の色付きの箱（AKBSの勤務表と同じ色） */
.badge {
  padding: 2px 4px;
  text-align: center;
  white-space: pre;
  border-radius: 2px;
}

/* 代勤の箱。左上に「代勤」の小さいバッジ（白地・灰色） */
.op-box {
  position: relative;
  margin-top: 5px;
}

.op-badge {
  position: absolute;
  top: -6px;
  left: -3px;
  font-size: 9px;
  line-height: 1;
  padding: 1px 3px;
  border-radius: 2px;
  background: #fff;
  color: #374151;
  border: 1px solid #4b5563;
  z-index: 1;
}

/* 時間帯がかぶる代勤と、かぶる日の今回の勤務（赤枠で目立たせる） */
.overlap-cell {
  border: 2px solid #dc2626;
  border-radius: 3px;
  padding: 2px 3px;
  background: #fef2f2;
}

.overlap-title {
  color: #dc2626;
  font-weight: bold;
  text-align: center;
}

.overlap-note {
  color: #dc2626;
  font-weight: bold;
  margin-top: 2px;
}

/* 代勤が確定しているか（確定は赤、未確定はグレー） */
.op-confirm {
  margin-top: 2px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.op-confirm.yes {
  color: #dc2626;
}

.op-confirm.no {
  color: #6b7280;
}

.overlap-diff {
  color: #374151;
  font-weight: bold;
  margin-top: 2px;
}

/* 引き継ぎ元に点線で薄く残す */
.ghost {
  border: 1px dashed #9ca3af;
  border-radius: 3px;
  padding: 2px;
  opacity: 0.4;
}

/* 操作の行 */
.ops-row th,
.ops-row td {
  background: #fff;
  border-top: 2px solid #c8c8c8;
}

.ops-row th {
  font-weight: bold;
  color: #374151;
}

.op-group + .op-group {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed #c8c8c8;
}

.op-label {
  font-weight: bold;
  color: #374151;
}

/* 操作の選択肢（丸いボタン。選んだものだけ indigo で塗りつぶす）
   選択中のクラスは selected。共通CSS の .on が緑（!important）なので使わない */
.action {
  display: block;
  width: 100%;
  margin-top: 3px;
  padding: 2px 6px;
  border: 1px solid #a5b4fc;
  border-radius: 9999px;
  background: #fff;
  color: #4338ca;
  font-size: 11px;
  line-height: 1.4;
  text-align: center;
  cursor: pointer;
}

/* ホバーは未選択だけ薄い色にする（選択中に効かせると白文字が読めなくなる） */
.action:hover:not(:disabled):not(.selected) {
  background: #eef2ff;
}

.action.selected {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
  font-weight: bold;
}

.action.selected:hover:not(:disabled) {
  background: #4338ca;
  border-color: #4338ca;
}

.action:disabled {
  cursor: default;
}

.action:disabled:not(.selected) {
  color: #9ca3af;
  border-color: #e5e7eb;
}

/* 一括で選ぶ行（枠の見出しの下。ボタンは日ごとの操作と同じ丸いボタンを横に並べる） */
.bulk-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  margin: 0 0 6px;
  font-size: 12px;
}

.bulk-label {
  font-weight: bold;
  color: #4b5563;
  margin-right: 4px;
}

.action.bulk {
  display: inline-block;
  width: auto;
  margin-top: 0;
  padding: 3px 12px;
  font-size: 12px;
}

/* 操作の結果の見せ方 */
.preview-task {
  border: 1px solid #888;
  background: #eee;
  color: #333;
  text-align: center;
  font-weight: bold;
  padding: 1px;
  border-radius: 3px;
  margin-top: 3px;
}

.preview-delete {
  color: #dc2626;
  text-align: center;
  font-weight: bold;
  margin-top: 2px;
}

/* 「削除する」を選んだ登録情報：中身だけ取消線と薄い表示にし、「削除します」の文字とメモには掛けない（メモは消す前に読めるように濃いまま。ユーザー希望） */
.deleted > *:not(.preview-delete):not(.note) {
  text-decoration: line-through;
  opacity: 0.5;
}

.note {
  margin-top: 3px;
  padding: 2px 4px;
  font-size: 10px;
  /* メモは濃い字で読めるように（まわりの薄い字を継がない。ユーザー希望） */
  color: #111827;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  white-space: pre-wrap;
  text-align: left;
}

.orange {
  background: orange;
  color: #fff;
  font-weight: bold;
}

.purple {
  background: purple;
  color: #fff;
  font-weight: bold;
}

.red {
  background: red !important;
  color: #fff !important;
  font-weight: bold !important;
}

.pink {
  background: #ff80c0;
  font-weight: bold;
}

.yellow {
  background: #ffff80;
  font-weight: bold;
}

.blue {
  background: #6cd5fd;
  font-weight: bold;
}

.red2 {
  background: #ff0000;
  font-weight: bold;
}

.gray {
  background: #c0c0c0 !important;
  font-weight: bold !important;
}

.orange2 {
  background: orange;
  font-weight: bold;
}

.green {
  background: #80ff80 !important;
  font-weight: bold !important;
}

.purple2 {
  background: purple;
  font-weight: bold;
}
</style>
